import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}

const TOAST_COLORS = {
  success: { bg: '#2e7d32', icon: '✓' },
  error: { bg: '#c62828', icon: '✕' },
  info: { bg: '#1565c0', icon: 'ℹ' },
  warning: { bg: '#e65100', icon: '⚠' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '@keyframes slideIn { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++counter.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div style={styles.container}>
        {toasts.map((t) => {
          const colors = TOAST_COLORS[t.type] || TOAST_COLORS.info;
          return (
            <div key={t.id} style={{ ...styles.toast, backgroundColor: colors.bg }}>
              <span style={styles.icon}>{colors.icon}</span>
              <span style={styles.message}>{t.message}</span>
              <button style={styles.close} onClick={() => removeToast(t.id)}>✕</button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

const styles = {
  container: {
    position: 'fixed', top: '16px', left: '16px', zIndex: 9999,
    display: 'flex', flexDirection: 'column', gap: '8px',
    direction: 'rtl',
  },
  toast: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    color: 'white', fontSize: '14px', fontWeight: '500',
    minWidth: '280px', maxWidth: '420px',
    animation: 'slideIn 0.3s ease',
  },
  icon: { fontSize: '16px', fontWeight: 'bold', flexShrink: 0 },
  message: { flex: 1, lineHeight: 1.4 },
  close: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
    cursor: 'pointer', fontSize: '14px', padding: '0', lineHeight: 1, flexShrink: 0,
  },
};
